import React from 'react';
import { FieldError } from '@spike/model';
import {
    AddOnServiceOrderItem,
    AppointmentOrderItem,
    CustomOrderItem,
    Discount,
    Order,
    OrderizableTypes,
    ProductOrderItem,
    SaveOrder
} from '@spike/order-model';
import { Client } from '@spike/client-model';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { useSingleSaleStyles } from 'components/Payments/SingleSale/SingleSale.styles';
import { SingleSaleHeader } from 'components/Payments/SingleSale/Header/SingleSaleHeader';
import { SingleSaleTable } from 'components/Payments/SingleSale/Table/SingleSaleTable';
import { ClientsState, ClientsStatus, getClientThunk } from '@spike/clients-action';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import moment from 'moment';
import { OverFullWindow } from 'components/UI';
import { Client as ClientPage } from 'pages/Client';
import { showErrorThunk } from '@spike/notifications-action';
import { isEmpty, uniq } from 'lodash';
import { convertToNumber } from '@spike/price-utils';
import { OrdersState, OrdersStatus, saveOrderThunk } from '@spike/orders-action';
import { v4 as uuid } from 'uuid';
import { SingleSaleSuccess } from 'components/Payments/SingleSale/Success/SingleSaleSuccess';
import Checkout from 'components/Checkout2';

export type GenericOrderItem = AppointmentOrderItem | ProductOrderItem | AddOnServiceOrderItem | CustomOrderItem;

export const isAppointmentOrderItem = (orderItem: GenericOrderItem): orderItem is AppointmentOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.APPOINTMENT;
};

export const isProductOrderItem = (orderItem: GenericOrderItem): orderItem is ProductOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.PRODUCT;
};

export const isAddOnServiceOrderItem = (orderItem: GenericOrderItem): orderItem is AddOnServiceOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.ADDON_SERVICE;
};

export const isCustomOrderItem = (orderItem: GenericOrderItem): orderItem is CustomOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.CUSTOM;
};

export const updateOrderItemDiscount = (orderItem: GenericOrderItem, discount: Discount) => {
    const orderItemData = {
        ...orderItem,
        orderItem: {
            ...orderItem.orderItem,
            discount
        }
    };

    return {
        ...orderItemData,
        orderItem: {
            ...orderItemData.orderItem,
            amount: getOrderItemAmount(orderItemData, true),
            total: getOrderItemSubtotal(orderItemData, true)
        }
    };
};

export const updateOrderItemQuantity = (orderItem: GenericOrderItem, quantity: number) => {
    const orderItemData = {
        ...orderItem,
        orderItem: {
            ...orderItem.orderItem,
            quantity
        }
    };

    return {
        ...orderItemData,
        orderItem: {
            ...orderItemData.orderItem,
            amount: getOrderItemAmount(orderItemData, true),
            total: getOrderItemSubtotal(orderItemData, true)
        }
    };
};

export const getOrderItemAmount = <T extends boolean = false>(
    orderItem: GenericOrderItem,
    format?: T
): T extends true ? string : number => {
    let amount = 0;

    if (isProductOrderItem(orderItem) && orderItem.product?.id) {
        amount = Number(orderItem.product.businessProduct?.pricing.price);
    }

    if (isAddOnServiceOrderItem(orderItem) && orderItem.addOnService?.id) {
        amount = Number(orderItem.addOnService.price);
    }

    if (isCustomOrderItem(orderItem) && orderItem.orderItem.amount) {
        amount = Number(orderItem.orderItem.amount);
    }

    if (format) {
        return amount.toFixed(2) as T extends true ? string : number;
    }

    return amount as T extends true ? string : number;
};

export const getOrderItemSubtotal = <T extends boolean = false>(
    item: GenericOrderItem,
    format?: T
): T extends true ? string : number => {
    let total = 0;

    total = getOrderItemAmount(item) * item.orderItem.quantity;

    if (item.orderItem.discount?.percentage) {
        total = total * (1 - item.orderItem.discount.percentage / 100);
    }

    if (item.orderItem.discount?.amount) {
        total = total - Number(item.orderItem.discount.amount);
    }

    if (format) {
        return total.toFixed(2) as T extends true ? string : number;
    }

    return total as T extends true ? string : number;
};

export const validateOrder = (order: Order): Array<FieldError> => {
    const errors: Array<FieldError> = [];

    const orderItems: Array<GenericOrderItem> = [
        ...order.items.appointments,
        ...order.items.products,
        ...order.items.addOnServices,
        ...order.items.customs
    ];

    if (order.client?.id === undefined) {
        errors.push({
            fieldName: `client`,
            errorMessage: `Select a client.`
        });
    }

    orderItems
        .filter(
            item =>
                (item.orderItem.discount?.amount &&
                    item.orderItem.total &&
                    convertToNumber(item.orderItem.discount.amount) >
                        convertToNumber(item.orderItem.amount) * item.orderItem.quantity) ||
                (item.orderItem.discount?.percentage && item.orderItem.discount.percentage > 100)
        )
        .forEach(item => {
            const totalNoDiscounts = Number(item.orderItem.amount) * Number(item.orderItem.quantity);

            errors.push({
                fieldName: `${item.orderItem.uuid}_discount`,
                errorMessage: `Discount cannot be greater than $${totalNoDiscounts.toFixed(2)}`
            });
        });

    orderItems
        .filter(item => isProductOrderItem(item) && item.product?.id === undefined)
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_selection`, errorMessage: `Select a product.` });
        });

    orderItems
        .filter(item => isAddOnServiceOrderItem(item) && item.addOnService?.id === undefined)
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_selection`, errorMessage: `Select an add-on service.` });
        });

    orderItems
        .filter(item => isProductOrderItem(item) && item.product?.id)
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isProductOrderItem(item) &&
                        isProductOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.product?.id === item.product?.id
                )
            )
                errors.push({ fieldName: `${item.orderItem.uuid}_duplicated`, errorMessage: `Duplicated product.` });
        });

    orderItems
        .filter(item => isAddOnServiceOrderItem(item) && item.addOnService?.id)
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isAddOnServiceOrderItem(item) &&
                        isAddOnServiceOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.addOnService?.id === item.addOnService?.id
                )
            )
                errors.push({
                    fieldName: `${item.orderItem.uuid}_duplicated`,
                    errorMessage: `Duplicated add-on service.`
                });
        });

    orderItems
        .filter(item => isCustomOrderItem(item) && isEmpty(item.orderItem.description))
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_description`, errorMessage: `Add a custom description.` });
        });

    orderItems
        .filter(item => isCustomOrderItem(item))
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isCustomOrderItem(item) &&
                        isCustomOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.orderItem.description === item.orderItem.description
                )
            )
                errors.push({
                    fieldName: `${item.orderItem.uuid}_duplicated`,
                    errorMessage: `Duplicated custom description.`
                });
        });

    orderItems
        .filter(
            item =>
                isCustomOrderItem(item) &&
                (isEmpty(item.orderItem.amount) || convertToNumber(item.orderItem.amount) === 0)
        )
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_price`, errorMessage: `Add a price.` });
        });

    return errors;
};

const createEmptyOrder = (client: Client, timeZone: string): Order => {
    return {
        id: 0,
        uuid: '',
        version: '',
        client: client,
        shipToClient: client,
        notes: '',
        items: {
            appointments: [],
            products: [],
            addOnServices: [],
            customs: []
        },
        issuedAt: moment().tz(timeZone),
        createdAt: moment().tz(timeZone),
        updatedAt: moment().tz(timeZone),
        total: '0.00',
        paid: false
    };
};

enum ViewOptions {
    SALE = 'sale',
    NEW_CLIENT = 'new_client',
    PAYMENTS = 'payments',
    SUCCESS = 'success'
}

export const SingleSale: React.FC = () => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();

    const classes = useSingleSaleStyles();

    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errors, setErrors] = React.useState<Array<FieldError>>([]);
    const [order, setOrder] = React.useState<Order | undefined>(undefined);
    const [selectedView, setSelectedView] = React.useState<ViewOptions>(ViewOptions.SALE);
    const [selectedClient, setSelectedClient] = React.useState<Client | undefined>(undefined);

    const [checkout, setCheckout] = React.useState<{
        orderId?: number;
        show: boolean;
        collapse: boolean;
        editClientId: number | null;
    }>({ show: false, collapse: false, editClientId: null });

    const orderState = useSelector<RootState, OrdersState>(state => state.orders);
    const clientState = useSelector<RootState, ClientsState>(state => state.clients);

    const savedOrder = useSelector<RootState, Order | undefined>(state => state.orders.savedOrder);

    const changeClientHandler = (clientId?: number) => {
        if (clientId) {
            setIsLoading(true);
            // Get client info, to create order.
            // Check useEffect for clientState.status
            dispatch(getClientThunk(apiClientWrapper, clientId));
        }

        if (!clientId) {
            setOrder(undefined);
            setSelectedClient(undefined);
        }
    };

    const clientSavedHandler = (client: Client) => {
        setSelectedView(ViewOptions.SALE);

        changeClientHandler(client.id);
    };

    const resetOrderHandler = () => {
        setOrder(undefined);
        setSelectedClient(undefined);
        setSelectedView(ViewOptions.SALE);
    };

    const payOrderHandler = () => {
        if (!order) {
            return;
        }

        const errors = validateOrder(order);
        setErrors(errors);

        uniq(errors.map(error => error.errorMessage)).forEach(error => dispatch(showErrorThunk(error)));

        if (errors.length > 0) {
            return;
        }

        const saveOrder: SaveOrder = {
            notes: '',
            uuid: uuid(),
            clientId: order.client.id!,
            shipToClientId: order.shipToClient.id!,
            items: [
                ...order.items.appointments,
                ...order.items.products,
                ...order.items.addOnServices,
                ...order.items.customs
            ].map(item => ({
                uuid: uuid(),
                orderizable: {
                    type: item.orderItem.orderizable.type,
                    id: isProductOrderItem(item)
                        ? item.product.businessProduct?.id
                        : isAddOnServiceOrderItem(item)
                        ? item.addOnService.id
                        : undefined
                },
                petId: item.pet?.id,
                taxId: item.orderItem.taxId,
                description: item.orderItem.description,
                quantity: item.orderItem.quantity,
                amount: item.orderItem.amount,
                discount: item.orderItem.discount,
                total: item.orderItem.total
            }))
        };

        setIsLoading(true);
        dispatch(saveOrderThunk(apiClientWrapper, saveOrder));
    };

    React.useEffect(() => {
        dispatch(fetchProductsThunk(apiClientWrapper));
        dispatch(fetchAddOnServicesThunk(apiClientWrapper));
    }, []);

    React.useEffect(() => {
        if (isLoading && checkout.editClientId === null && clientState.status === ClientsStatus.GetSuccess) {
            const clientInfo = clientState.client as Client;

            setIsLoading(false);
            setSelectedClient(clientInfo);
            setOrder(createEmptyOrder(clientInfo, timeZone));
        }
    }, [clientState.status]);

    React.useEffect(() => {
        if (isLoading && orderState.status === OrdersStatus.SaveOrderSuccess) {
            // TODO: Add checkout & payments logic here
            setIsLoading(false);
            setCheckout({ orderId: savedOrder?.id, show: true, collapse: false, editClientId: null });
        }
    }, [orderState.status]);

    if (selectedView === ViewOptions.NEW_CLIENT) {
        return (
            <OverFullWindow onClose={() => setSelectedView(ViewOptions.SALE)}>
                <ClientPage
                    hideHeader={true}
                    onClientSaved={clientSavedHandler}
                    onClose={() => setSelectedView(ViewOptions.SALE)}
                    onPetSaved={() => setSelectedView(ViewOptions.SALE)}
                />
            </OverFullWindow>
        );
    }

    if (selectedView === ViewOptions.SUCCESS) {
        return (
            <SingleSaleSuccess
                order={savedOrder!}
                onBack={resetOrderHandler}
            />
        );
    }

    const checkoutPaidHandler = () => {
        setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false, editClientId: null }));
        setSelectedView(ViewOptions.SUCCESS);
    };

    const checkoutPayByLinkSentHandler = () => {
        setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false, editClientId: null }));
        resetOrderHandler();
    };

    const checkoutEditClientHandler = (clientId: number) => {
        setCheckout(prev => ({ ...prev, collapse: true, editClientId: clientId }));
    };

    const closeCheckoutEditClientHandler = () => {
        setCheckout(prev => ({ ...prev, collapse: false, editClientId: null }));
    };

    return (
        <>
            <div className={classes.root}>
                <SingleSaleHeader
                    errors={errors}
                    data={selectedClient!}
                    onAddNewClient={() => setSelectedView(ViewOptions.NEW_CLIENT)}
                    onClientSelected={changeClientHandler}
                />

                {order && (
                    <SingleSaleTable
                        order={order}
                        errors={errors}
                        isLoading={isLoading}
                        onChange={setOrder}
                        onPay={payOrderHandler}
                    />
                )}
            </div>
            {checkout.show && checkout.orderId && (
                <Checkout
                    orderId={checkout.orderId}
                    type="singlesale"
                    onClose={() => setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false }))}
                    onPaid={checkoutPaidHandler}
                    onPayByLinkSent={checkoutPayByLinkSentHandler}
                    hide={checkout.collapse}
                    onEditClient={checkoutEditClientHandler}
                />
            )}
            {checkout.editClientId !== null && (
                <OverFullWindow onClose={closeCheckoutEditClientHandler}>
                    <ClientPage
                        clientId={checkout.editClientId}
                        hideHeader={true}
                        onCloseModal={closeCheckoutEditClientHandler}
                        onClientSaved={closeCheckoutEditClientHandler}
                    />
                </OverFullWindow>
            )}
        </>
    );
};
